import React, { useCallback, useEffect, useState } from "react";
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  MenuItem,
} from "@mui/material";
import useApi from "../../hooks/useApi";
import ProductionOtherCostModal from "./ProductionOtherCostModal";
const ProductionGeneral = ({
  productionData,
  setPurchaseInvoiceData,
  BASE_URL,
  COMPANY_CODE,
  DEPARTMENT,
  unitCostManual,
  setUnitCostManual,
  otherCosts,
  setOtherCosts,
  mode,
  isOtherCostModalOpen,
  setIsOtherCostModalOpen,
  currency,
  currencyRate,
}) => {
  const { request, loading } = useApi();
  const [glSettings, setGlSettings] = useState([]);
  const [productionType, setProductionType] = useState("ASSEMBLING");

  const fetchGLSettings = useCallback(async () => {
    const url = `${BASE_URL}/api/CRMGLSettings/${COMPANY_CODE}/SCRN_CODE/IN/-/${DEPARTMENT}/-`;
    try {
      const response = await request({ url, method: "GET", timeout: 10000 });
      setGlSettings(response || []);
    } catch (error) {
      console.error("Failed to fetch GL settings", error);
      setGlSettings([]);
    }
  }, [BASE_URL, COMPANY_CODE, DEPARTMENT]);

  const [locations, setLocations] = useState([]);

  const fetchLocations = useCallback(async () => {
    const url = `${BASE_URL}/api/CpaysCount/${COMPANY_CODE}/LOCATONLIST_ALL/-/-`;
    try {
      const response = await request({ url, method: "GET", timeout: 10000 });
      setLocations(response || []);
    } catch (error) {
      console.error("Failed to fetch locations", error);
      setLocations([]);
    }
  }, [BASE_URL, COMPANY_CODE]);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  useEffect(() => {
    if (locations.length > 0 && !productionData?.location) {
      setPurchaseInvoiceData((prev) => ({
        ...prev,
        location: locations[0].loc_code,
      }));
    }
  }, [locations, productionData?.location, setPurchaseInvoiceData]);

  useEffect(() => {
    if (mode === "edit") return;
    fetchGLSettings();
  }, [fetchGLSettings, mode]);

  const getINAccounts = (gls) => {
    const findAcc = (code) =>
      gls.find((x) => x.Scrn_code === code)?.ACCOUNT?.trim() || "########";

    const stockAccount = findAcc("IN-STK-C");

    return {
      debitAcc: stockAccount,
      creditAcc: stockAccount,
    };
  };

  useEffect(() => {
    if (!glSettings.length) return;
    if (mode === "edit") return;

    const { debitAcc, creditAcc } = getINAccounts(glSettings);

    setPurchaseInvoiceData((prev) => ({
      ...prev,
      drAccount: debitAcc,
      crAccount: creditAcc,
    }));
  }, [glSettings, setPurchaseInvoiceData, mode]);

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-4">
        {/* Section 1 - Production Info */}
        <div className="lg:col-span-4 col-span-1 flex flex-col gap-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-sm text-gray-600">Production No:</span>
              <span className="text-red-500 font-semibold">
                {productionData?.documentNo}
              </span>
            </div>
            <TextField
              label="Date"
              type="date"
              variant="outlined"
              size="small"
              value={productionData?.date}
              onChange={(e) =>
                setPurchaseInvoiceData((prev) => ({ ...prev, date: e.target.value }))
              }
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </div>
          <div className="flex items-center gap-4">
            <TextField
              label="Estimation Number"
              variant="outlined"
              size="small"
              value={productionData?.estimationNumber}
              fullWidth
            />

            <TextField
              label="WO No"
              variant="outlined"
              size="small"
              value={productionData?.woNo}
              onChange={(e) =>
                setPurchaseInvoiceData((prev) => ({
                  ...prev,
                  woNo: e.target.value,
                }))
              }
              // InputProps={{
              //   readOnly: true,
              // }}
              fullWidth
            />
          </div>
          <div className="p-3 bg-gray-100 flex justify-between rounded-lg">
            <div>
              <span className="text-sm text-gray-600">Credit A/C:</span>{" "}
              <span className="font-bold text-orange-600">
                {productionData?.crAccount || "######"}
              </span>
            </div>
            <div>
              <span className="text-sm text-gray-600">Debit A/C:</span>
              <span className="font-bold text-green-600">
                {productionData?.drAccount || "#####"}
              </span>
            </div>
          </div>
        </div>

        {/* Section 2 - Estimation & WO */}
        <div className="lg:col-span-4 col-span-1 flex flex-col gap-4">
          <TextField
            label="Product Code"
            variant="outlined"
            size="small"
            value={productionData?.productCode}
            onChange={(e) =>
              setPurchaseInvoiceData((prev) => ({
                ...prev,
                productCode: e.target.value,
              }))
            }
            InputProps={{
              readOnly: true,
            }}
            fullWidth
          />

          <TextField
            label="Product Description"
            variant="outlined"
            size="small"
            value={productionData?.productDescription}
            onChange={(e) =>
              setPurchaseInvoiceData((prev) => ({
                ...prev,
                productDescription: e.target.value,
              }))
            }
            fullWidth
            multiline
            rows={2}
            // InputProps={{
            //   readOnly: true,
            // }}
          />
        </div>

        {/* Section 3 - Production Details & Tracking */}
        <div className="lg:col-span-4 col-span-1 flex flex-col gap-4">
          <div className="flex gap-4">
            <TextField
              label="Production Qty"
              variant="outlined"
              size="small"
              type="number"
              value={productionData?.productionQty}
              onChange={(e) => {
                const val = Number(e.target.value || 0);
                setPurchaseInvoiceData((prev) => ({
                  ...prev,
                  productionQty: val,
                }));
              }}
              fullWidth
              InputProps={{
                readOnly: true,
              }}
            />
            {/* Hide Unit Cost if useraccess contains AC, SW, or ST */}
            {(() => {
              let userAccess = "";
              try {
                const userObj = JSON.parse(localStorage.getItem("currentUser"));
                userAccess = userObj?.[0]?.useraccess || "";
              } catch (e) {}
              const shouldHideCost = ["AC", "SW", "ST"].some((code) => userAccess.includes(code));
              if (shouldHideCost) return null;
              return (
                <TextField
                  label="Unit Cost"
                  variant="outlined"
                  size="small"
                  type="number"
                  value={productionData?.unitCost ?? ""}
                  onChange={(e) => {
                    const val = Number(e.target.value || 0);
                    setPurchaseInvoiceData((prev) => ({ ...prev, unitCost: val }));
                  }}
                  fullWidth
                  InputProps={{
                    readOnly: true,
                  }}
                />
              );
            })()}
          </div>
          <div className="flex gap-4">
            {/* <TextField
              select
              label="Status"
              variant="outlined"
              size="small"
              value={productionData?.jobCode || ""}
              onChange={(e) =>
                setPurchaseInvoiceData((prev) => ({
                  ...prev,
                  jobCode: e.target.value,
                }))
              }
              fullWidth
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              <MenuItem value="In Progress">In Progress</MenuItem>
              <MenuItem value="Completed">Completed</MenuItem>
            </TextField> */}
            <TextField
              select
              label="Production Location"
              variant="outlined"
              size="small"
              value={productionData?.location || ""}
              onChange={(e) =>
                setPurchaseInvoiceData((prev) => ({
                  ...prev,
                  location: e.target.value,
                }))
              }
              fullWidth
            >
              {locations?.map((loc) => (
                <MenuItem key={loc.loc_code} value={loc.loc_code}>
                  {loc.DESC}
                </MenuItem>
              ))}
            </TextField>
          </div>
          <div className=" gap-4 grid grid-cols-2">
            <TextField
              label="Other Costs"
              variant="outlined"
              size="small"
              value={productionData?.otherCostsTotal ?? "0.00"}
              fullWidth
              InputProps={{ readOnly: true }}
            />
            <button
              onClick={() => setIsOtherCostModalOpen(true)}
              className=" p-2 bg-blue-600 rounded-lg text-white"
            >
              + Add Oth.Cost
            </button>
          </div>
        </div>
        <ProductionOtherCostModal
          open={isOtherCostModalOpen}
          onClose={() => setIsOtherCostModalOpen(false)}
          otherCosts={otherCosts}
          setOtherCosts={setOtherCosts}
          BASE_URL={BASE_URL}
          COMPANY_CODE={COMPANY_CODE}
          DEPARTMENT={DEPARTMENT}
          invNo={productionData?.documentNo}
          pdate={productionData?.date}
          jobNo={productionData?.jobCode}
          currency={currency}
          currencyRate={currencyRate}
        />
      </div>
    </>
  );
};

export default ProductionGeneral;
